export type Severity = 'fail' | 'warn' | 'info';
export type FindingStatus = 'pass' | 'warn' | 'fail' | 'skipped';

export type CategoryId =
  | 'structure'
  | 'name'
  | 'description'
  | 'body'
  | 'triggers'
  | 'lexical'
  | 'progressiveDisclosure'
  | 'safety'
  | 'discoverability';

export interface ParsedFrontmatter {
  frontmatter: Record<string, unknown>;
  body: string;
  rawFrontmatter: string;
  parseError: string | null;
}

import type { TextMetrics } from '../analysis/text';
import type { TriggerMetrics } from '../analysis/triggers';
import type { ArchitectureMetrics } from '../analysis/architecture';
import type { CohesionMetrics } from '../analysis/cohesion';
import type { SemanticMetrics } from '../analysis/semantic';
import type { StyleMetrics } from '../analysis/style';

export interface SubfolderEntry { name: string; type: 'file' | 'dir'; size?: number }

// Optional structured-skill fields (carried through the adapter from a host
// Skill object). When the scorer is invoked with a raw SKILL.md string these
// stay undefined and discoverability rules degrade gracefully.
export interface SkillMetadata {
  tags?: string[];
  category?: string;
  mcpTools?: string[];
}

export interface ScoringContext {
  source: 'paste' | 'github';
  skillMd: string;
  parsed: ParsedFrontmatter;
  directoryListing?: string[];
  folderName?: string;
  skillFilename?: string;
  subfolderEntries?: Record<string, SubfolderEntry[]>;
  descText: TextMetrics;
  bodyText: TextMetrics;
  triggers: TriggerMetrics;
  arch: ArchitectureMetrics;
  cohesion: CohesionMetrics;
  semantic: SemanticMetrics;
  style: StyleMetrics;
  /** Set when the scorer is invoked with a structured Skill object. */
  skillMetadata?: SkillMetadata;
}

export interface Finding {
  id: string;
  category: CategoryId;
  severity: Severity;
  status: FindingStatus;
  weight: number;
  title: string;
  message: string;
  fix?: string;
  /** Citation to the relevant section of Anthropic's skill-building guide. */
  guideRef?: string;
}

export interface Rule {
  id: string;
  category: CategoryId;
  severity: Severity;
  weight: number;
  title: string;
  appliesTo?: 'paste' | 'github' | 'both';
  /** Citation to the relevant section of Anthropic's skill-building guide. */
  guideRef?: string;
  run(ctx: ScoringContext): Omit<Finding, 'id' | 'category' | 'severity' | 'weight' | 'title'>;
}

export interface CategoryScore {
  id: CategoryId;
  label: string;
  weight: number;
  earned: number;
  ratio: number;
}

export interface Recommendation {
  id: string;
  title: string;
  why: string;
  fix: string;
  severity: Severity;
}

export type Grade = 'Excellent' | 'Strong' | 'Decent' | 'Needs work' | 'Critical issues';
export type LetterGrade = 'A' | 'B' | 'C' | 'D' | 'F';

export interface ScoreReport {
  score: number;
  grade: Grade;
  /** Letter-grade view of the same score (A–F). Useful for compact badges. */
  letterGrade: LetterGrade;
  categories: CategoryScore[];
  findings: Finding[];
  recommendations: Recommendation[];
  parseError: string | null;
  /** Version of the merged ruleset, for cache invalidation. */
  rulesetVersion: string;
  assessedAt: Date;
}

export const CATEGORY_LABELS: Record<CategoryId, string> = {
  structure: 'Structure',
  name: 'Name',
  description: 'Description',
  triggers: 'Trigger surface',
  body: 'Body quality',
  lexical: 'Lexical quality',
  progressiveDisclosure: 'Progressive disclosure',
  safety: 'Safety & metadata',
  discoverability: 'Discoverability',
};

// Description (22) > Structure (18) > Triggers (15) > Body (14) > Lexical (10) >
// Name (8) > PD (8) > Discoverability (5) > Safety (5). Total = 105.
export const CATEGORY_WEIGHTS: Record<CategoryId, number> = {
  structure: 18,
  name: 8,
  description: 22,
  triggers: 15,
  body: 14,
  lexical: 10,
  progressiveDisclosure: 8,
  safety: 5,
  discoverability: 5,
};
